import { BigNumber } from "shared/cubiq-model-multi-asset";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { ICcyPairLookupService } from "../model/ICcyPairLookupService";
import { RateCache, CURVE_SPT } from "../../rate/impl/RateCache";
import { Rate } from "../../rate/model/Rate";
import { ITraderGroupLookupService } from "../model/ITraderGroupLookupService";
import { UserTemperatureService } from "../../profile/impl/UserTemperatureService";
import { OrderDataImpl, OrderIntent } from "shared/cubiq-model-multi-asset";
import { TranslateService } from "@ngx-translate/core";

import { LoggerService } from "@bfx/logger";
import { ZERO } from "shared/base-components/number-spinner.component";
const logger = LoggerService.for(__dirname);

export const EMPTY_RATE_STRING = "-";

const STRIKETHROUGH_SUFFIX = "--STRIKETHROUGH--";
const STRIKETHROUGH_INCREASE = 1000000;

const ZERO_RATE: BigNumber = new BigNumber("0");

const ABOVE_ACTIVE_BIN: number = -15e4; //-15000
const ABOVE_ALGO_ACTIVE_BIN: number = -13e4;
const ABOVE_MKT_PENDING_BIN: number = -11e4;
const ABOVE_TRIGGER_BIN: number = -9e4;
const ABOVE_TRIGGER_MKT_BIN: number = -7e4;

const BELOW_TRIGGER_MKT_BIN: number = 7e4;
const BELOW_TRIGGER_BIN: number = 9e4;
const BELOW_MKT_PENDING_BIN: number = 11e4;
const BELOW_ALGO_ACTIVE_BIN: number = 13e4;
const BELOW_ACTIVE_BIN: number = 15e4;

export enum PricingMode {
  Blotter,
  Ladder,
  LadderInverted,
}

export enum PricingServiceMode {
  BASE,
  DEALT,
}

export interface IFormattedMarkup {
  numValue: BigNumber;

  textValue: string;
}

/**
 * Base service for Dealt/Base pricing services
 */
export class PricingService {
  private modeIsDealt: boolean;

  /* this will also take a rate service for market rates and fwd pts? */
  constructor(
    private myCcyPairLookup: ICcyPairLookupService,
    private myTraderGroupLookup: ITraderGroupLookupService,
    private rateCache: RateCache,
    private userTemps: UserTemperatureService,
    mode: PricingServiceMode = PricingServiceMode.BASE,
    private _translationService: TranslateService,
  ) {
    this.modeIsDealt = mode === PricingServiceMode.DEALT;
  }

  public isAlgo(order: any): boolean {
    return CubiqTS.OrderType.ALGO === order.orderType;
  }

  public isMktOrder(order: any): boolean {
    return (
      CubiqTS.OrderType.MARKET === order.orderType ||
      CubiqTS.OrderType.MARKET_FOK === order.orderType ||
      CubiqTS.OrderType.MARKET_IOC === order.orderType
    );
  }

  /* Exposed Interface methods accessible by adapters  */
  public isTriggered(order: any): boolean {
    return (
      CubiqTS.OrderState.TRIGGERED == order.orderState ||
      CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED == order.orderState ||
      CubiqTS.OrderState.MANUAL_PRICING == order.orderState
    );
  }

  public isAboveMarket(order: CubiqTS.OrderDataImpl): boolean {
    // need to do this still
    return this.isBlotterAboveMarket(order);
  }

  private buyBase(order: CubiqTS.OrderDataImpl): boolean {
    return order.baseCode == order.fixedCode && CubiqTS.OrderIntent.BUY === order.clientIntent;
  }
  private sellBase(order: CubiqTS.OrderDataImpl): boolean {
    return order.baseCode == order.fixedCode && CubiqTS.OrderIntent.SELL === order.clientIntent;
  }
  private buyNonBase(order: CubiqTS.OrderDataImpl): boolean {
    return order.nonBaseCode == order.fixedCode && CubiqTS.OrderIntent.BUY === order.clientIntent;
  }
  private sellNonBase(order: CubiqTS.OrderDataImpl): boolean {
    return order.nonBaseCode == order.fixedCode && CubiqTS.OrderIntent.SELL === order.clientIntent;
  }

  private clientBuyingBase(order: CubiqTS.OrderDataImpl): boolean {
    if (this.modeIsDealt && order.isInvertedOrderFg) {
      return this.sellBase(order) || this.buyNonBase(order);
    }
    return this.buyBase(order) || this.sellNonBase(order);
  }

  private isTp(order: CubiqTS.OrderDataImpl): boolean {
    return (
      CubiqTS.OrderType.LIMIT_TP === order.orderType ||
      (CubiqTS.OrderType.ALGO === order.orderType &&
        CubiqTS.OrderType.LIMIT_TP === order.monitoringOrderType) ||
      // Per OMS-27240, ALGO without monitoringOrderType are TPs for above/below market sorting
      (CubiqTS.OrderType.ALGO === order.orderType && null == order.monitoringOrderType)
    );
  }

  private isSL(order: CubiqTS.OrderDataImpl): boolean {
    return (
      CubiqTS.OrderType.LIMIT_SL === order.orderType ||
      (CubiqTS.OrderType.ALGO === order.orderType &&
        CubiqTS.OrderType.LIMIT_SL === order.monitoringOrderType)
    );
  }

  private isCallAboveMarket(order: CubiqTS.OrderDataImpl): boolean {
    if (CubiqTS.OrderType.LIMIT_CALL === order.orderType) {
      if (this.modeIsDealt && order.isInvertedOrderFg) {
        return CubiqTS.RateSideType.BID == order.trackingSide;
      } else {
        return CubiqTS.RateSideType.OFFER == order.trackingSide;
      }
    }
    return false;
  }

  public isBlotterAboveMarket(order: CubiqTS.OrderDataImpl): boolean {
    return (
      ((this.isTp(order) || this.isMktOrder(order)) && !this.clientBuyingBase(order)) ||
      (this.isSL(order) && this.clientBuyingBase(order)) ||
      this.isCallAboveMarket(order)
    );
  }

  public isLadderAboveMarket(order: CubiqTS.OrderDataImpl): boolean {
    return !this.isBlotterAboveMarket(order);
  }

  public getInvertMarketMidRate(order: OrderDataImpl): string {
    let blotterRate: string = this.getBlotterMarketMidRate(order);
    if (blotterRate != EMPTY_RATE_STRING) {
      let ccyPair = this.getCcyPair(order);
      if (ccyPair) {
        let inverRate = new CubiqTS.BigNumber(1).dividedBy(blotterRate);
        let minPrecision: number = this.getMinInvertPrecision(ccyPair);
        let maxPrecision: number = this.getMaxInvertPrecision(ccyPair);

        // Order requested rate does not change with base vs dealt conv., spot tracking does
        return this.roundRateToPrecision(inverRate, minPrecision, maxPrecision);
      }
    }
    return blotterRate;
  }

  public getBlotterMarketMidRate(order: OrderDataImpl): string {
    let rate: Rate = this.rateCache.getRateByKey(this.getSpotRateKey(order));
    let midRate: CubiqTS.BigNumber;

    if (rate) {
      midRate = new CubiqTS.BigNumber(rate.midRate);
      if (CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType) {
        let fwdPts: any = this.getMidFwdPts(order);
        if (fwdPts !== null) {
          midRate = midRate.plus(fwdPts);
        }
      }
      if (order.isInvertedOrderFg && this.modeIsDealt) {
        midRate = new CubiqTS.BigNumber(1).dividedBy(midRate);
      }
      let ccyPair = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);

        // Order requested rate does not change with base vs dealt conv., spot tracking does
        return this.roundRateToPrecision(midRate, minPrecision, maxPrecision);
      } else {
        return midRate.toString();
      }
    }
    return EMPTY_RATE_STRING;
  }

  private getSpotRateKey(order: OrderDataImpl): string {
    if (order["spotRateKey"] == null) {
      let assetCode = order.baseCode + order.nonBaseCode;
      let curveId: string = CURVE_SPT;
      let key: string = assetCode + "-" + curveId + "-" + CubiqTS.Tenor[CubiqTS.Tenor.SPOT];
      order["spotRateKey"] = key;
      return key;
    } else {
      return order["spotRateKey"];
    }
  }

  public isSpotRateTradeable(order: OrderDataImpl): boolean {
    let rate: Rate = this.rateCache.getRateByKey(this.getSpotRateKey(order));

    return rate != null && rate.tradable;
  }

  public isFwdRateTradeable(order: OrderDataImpl): boolean {
    let fwdRate: Rate = this.rateCache.hasDetailFillFwdRate(order)
      ? this.rateCache.getDetailFillFwdRate(order)
      : this.rateCache.getRateByKey(this.rateCache.getFwdRateKey(order));

    return fwdRate != null && fwdRate.tradable;
  }

  public isFwdRateNdfTrackingTradeable(order: OrderDataImpl): boolean {
    let fwdRateNdfTracking: Rate = this.rateCache.hasDetailFillNdfRate(order)
      ? this.rateCache.getDetailFillNdfRate(order)
      : this.rateCache.getRateByKey(this.rateCache.getFwdRateNdfTrackingKey(order));

    return fwdRateNdfTracking != null && fwdRateNdfTracking.tradable;
  }

  public isFwdRateApplicable(order: OrderDataImpl): boolean {
    return (
      (CubiqTS.Product.FWD === order.product || CubiqTS.Product.NDF === order.product) &&
      !this.isSpotOrder(order)
    );
  }

  // Returns the rate on the order specified by column property,
  // rounded to the appropriate precision of the pair in dealt convention
  public getOrderRequestRt(order: CubiqTS.OrderDataImpl): string | CubiqTS.BigNumber {
    if (this.myCcyPairLookup && order.requestRt != undefined && order.requestRt != null) {
      let ccyPair = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);
        let rate = new BigNumber(order.requestRt);

        // Order requested rate does not change with base vs dealt conv., spot tracking does
        return this.roundRateToPrecision(rate, minPrecision, maxPrecision);
      }
    }

    if (order.requestRt != null) return order.requestRt;

    return EMPTY_RATE_STRING;
  }

  public getVwap(order: any, property: string): any {
    let value = order[property];

    if (this.myCcyPairLookup && value) {
      let ccyPair = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);
        let rate = new BigNumber(value);

        return this.roundRateToPrecision(rate, minPrecision, maxPrecision);
      }
    }
    if (value) {
      return value;
    }

    return EMPTY_RATE_STRING;
  }

  public getMarketVwapPoints(orderData: OrderDataImpl): any {
    if (orderData?.marketVwapAllInRt && orderData?.marketVwapSpotRt) {
      return orderData.marketVwapAllInRt.minus(orderData.marketVwapSpotRt);
    }

    return EMPTY_RATE_STRING;
  }

  public getClientVwapPoints(orderData: OrderDataImpl): any {
    if (orderData?.vwapAllInRt && orderData?.vwapSpotRt) {
      return orderData.vwapAllInRt.minus(orderData.vwapSpotRt);
    }

    return EMPTY_RATE_STRING;
  }

  public getVwapDealt(order: any): any {
    // TODO Confirm Presence of field with KT
    if (this.myCcyPairLookup && order.vwapAllInRt) {
      let ccyPair = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);
        let rate = new BigNumber(order.vwapAllInRt);

        return this.roundRateToPrecision(rate, minPrecision, maxPrecision);
      }
    }
    if (order.vwapAllInRt) {
      return order.vwapAllInRt;
    }

    return EMPTY_RATE_STRING;
  }

  public getVwapBase(order: any, inverted: boolean = false): any {
    // TODO Confirm Presence of field with KT
    if (this.myCcyPairLookup && order.vwapAllInRt) {
      let ccyPair = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, false);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, false);
        let rate = new BigNumber(order.vwapAllInRt);
        if (order.isInvertedOrderFg != inverted) {
          rate = new BigNumber("1").dividedBy(rate);
        }
        return this.roundRateToPrecision(rate, minPrecision, maxPrecision);
      }
    }
    if (order.vwapAllInRt) {
      return order.vwapAllInRt;
    }

    return EMPTY_RATE_STRING;
  }

  public getBlotterSpotTracking(order: any): any {
    let dfPtsLock = true;
    let round = true;
    let includePtsForSptFwds = false;

    if (this.modeIsDealt) {
      return this.getDealtSpotTracking(order, includePtsForSptFwds, dfPtsLock);
    }

    return this.getBaseSpotTracking(order, includePtsForSptFwds, round, dfPtsLock);
  }

  private getNonRoundedInvertSpotTracking(
    order: any,
    includePtsForSptFwds: boolean = false,
    dfPtsLock: boolean = false,
  ): any {
    let spotTracking = this.getBaseSpotTracking(order, includePtsForSptFwds, false, dfPtsLock);
    if (!spotTracking || spotTracking === EMPTY_RATE_STRING) {
      return EMPTY_RATE_STRING;
    }
    spotTracking = new CubiqTS.BigNumber(1).dividedBy(spotTracking);
    return spotTracking.toString();
  }

  public getInvertSpotTracking(
    order: any,
    includePtsForSptFwds: boolean = false,
    round: boolean = true,
    dfPtsLock: boolean = false,
  ): any {
    let spotTracking = this.getMarkedUpRequestRt(order);
    if (!spotTracking || spotTracking === EMPTY_RATE_STRING) {
      return EMPTY_RATE_STRING;
    }
    if (order.isInvertedOrderFg) {
      spotTracking = spotTracking;
    } else {
      spotTracking = new BigNumber("1").dividedBy(spotTracking);
    }
    if (
      includePtsForSptFwds ||
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType
    ) {
      let fwdPts: any = this.getInvertFwdPts(order, dfPtsLock);

      if (fwdPts !== null) {
        spotTracking = spotTracking.minus(fwdPts);
      }
    }
    if (round) {
      let ccyPair: any = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinInvertPrecision(ccyPair);
        let maxPrecision: number = this.getMaxInvertPrecision(ccyPair);
        return this.roundRateToPrecision(spotTracking, minPrecision, maxPrecision);
      }
    }

    return spotTracking.toString();
  }

  public getDealtSpotTracking(
    order: any,
    includePtsForSptFwds: boolean = false,
    dfPtsLock: boolean = false,
  ): any {
    if (order.isInvertedOrderFg == false) {
      return this.getBaseSpotTracking(order, includePtsForSptFwds, true, dfPtsLock);
    }
    let spotTracking = this.getInvertFwdTracking(order);
    if (
      spotTracking != EMPTY_RATE_STRING &&
      (includePtsForSptFwds ||
        CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType)
    ) {
      let fwdPts: any = this.getInvertFwdPts(order, dfPtsLock);

      if (fwdPts !== null) {
        spotTracking = new BigNumber(spotTracking).minus(fwdPts);
      }
    }
    if (spotTracking != EMPTY_RATE_STRING) {
      let ccyPair: any = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinInvertPrecision(ccyPair);
        let maxPrecision: number = this.getMaxInvertPrecision(ccyPair);
        return this.roundRateToPrecision(spotTracking, minPrecision, maxPrecision);
      }
    }

    return spotTracking;
  }

  // Order Details - FwdPts
  public getDealtFormatedFwdPts(order: CubiqTS.OrderDataImpl): string {
    if (this.isMktOrder(order)) return null;

    let ccyPair: any = this.getCcyPair(order);
    let dfPtsLock = true;
    let fwdPts = this.getDealtFwdPts(order, dfPtsLock);

    if (fwdPts === null) {
      if (order.forwardDetails) fwdPts = order.forwardDetails.currentMktPts;
      if (null == fwdPts) {
        return null;
      }
    }

    if (ccyPair && !this.isSpotOrder(order)) {
      let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
      let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);

      if (fwdPts) {
        if (order.isInvertedOrderFg) {
          let spotTracking = this.getDealtSpotTracking(order, true, dfPtsLock);
          let fwdTracking = this.getDealtFwdTracking(order);
          if (spotTracking == EMPTY_RATE_STRING || fwdTracking == EMPTY_RATE_STRING) {
            return null;
          }
          return this.roundRateToPrecision(
            new CubiqTS.BigNumber(spotTracking).minus(fwdTracking).times(-1),
            minPrecision,
            maxPrecision,
          );
        }

        return this.roundRateToPrecision(fwdPts, minPrecision, maxPrecision);
      }
    }
    return EMPTY_RATE_STRING;
  }

  // Order Details - Spot Tracking
  public getDealtFormatedSpotTracking(order: CubiqTS.OrderDataImpl): any {
    if (order.isInvertedOrderFg == false) {
      return this.getBaseSpotTracking(order);
    }
    let spotTracking = this.getInvertFwdTracking(order);
    if (
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType &&
      spotTracking != EMPTY_RATE_STRING &&
      order.isInnerEncoding
    ) {
      let fwdPts: any = this.getDealtFormatedFwdPts(order);

      if (fwdPts !== null) {
        spotTracking = new BigNumber(spotTracking).minus(fwdPts);
      }
    }

    if (spotTracking != EMPTY_RATE_STRING) {
      let ccyPair: any = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinInvertPrecision(ccyPair);
        let maxPrecision: number = this.getMaxInvertPrecision(ccyPair);
        return this.roundRateToPrecision(spotTracking, minPrecision, maxPrecision);
      }
    }

    return spotTracking;
  }

  public getBaseSpotTracking(
    order: any,
    includePtsForSptFwds: boolean = false,
    round: boolean = true,
    dfPtsLock: boolean = false,
  ): any {
    let spotTracking = this.getMarkedUpRequestRt(order);
    if (!spotTracking || spotTracking === EMPTY_RATE_STRING) {
      return EMPTY_RATE_STRING;
    }
    if (order.isInvertedOrderFg) {
      spotTracking = new BigNumber("1").dividedBy(spotTracking);
    } else {
      spotTracking = spotTracking;
    }
    if (
      includePtsForSptFwds ||
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType
    ) {
      let fwdPts: any = this.getBaseFwdPts(order, dfPtsLock);

      if (fwdPts !== null) {
        if (CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType) {
          spotTracking = spotTracking.minus(fwdPts);
        } else {
          spotTracking = spotTracking.plus(fwdPts);
        }
      }
    }
    if (round) {
      let ccyPair: any = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, false);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, false);
        return this.roundRateToPrecision(spotTracking, minPrecision, maxPrecision);
      }
    }

    return spotTracking.toString();
  }

  public getBaseNdfTracking(
    order: any,
    includePtsForSptFwds: boolean = false,
    round: boolean = true,
    dfPtsLock: boolean = false,
  ): any {
    let ndfTracking: CubiqTS.BigNumber | null = this.getMarkedUpRequestRt(order);
    if (!ndfTracking) {
      return EMPTY_RATE_STRING;
    }
    if (order.isInvertedOrderFg) {
      ndfTracking = new BigNumber("1").dividedBy(ndfTracking);
    } else {
      ndfTracking = ndfTracking;
    }
    let fwdPtsNdfTracking: BigNumber = this.getBaseNdfTrackingFwdPts(order, dfPtsLock);
    if (
      includePtsForSptFwds ||
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType
    ) {
      let fwdPts: any = this.getBaseFwdPts(order, dfPtsLock);
      if (fwdPts !== null) {
        ndfTracking = ndfTracking.minus(fwdPts);
      }
      if (fwdPtsNdfTracking !== null) {
        // might be struckthrough if no fwdPts, but apply all available
        ndfTracking = ndfTracking.plus(fwdPtsNdfTracking);
      }
    } else if (
      CubiqTS.RequestTrackingType.SPOT_RATE === order.requestTrackingType ||
      CubiqTS.Tenor.SPOT === order.settleTenor
    ) {
      if (fwdPtsNdfTracking !== null) {
        ndfTracking = ndfTracking.plus(fwdPtsNdfTracking);
      }
    }
    if (round) {
      let ccyPair: any = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, false);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, false);
        return this.roundRateToPrecision(ndfTracking, minPrecision, maxPrecision);
      }
    }
    return ndfTracking.toString();
  }

  public getDealtNdfTracking(order: any, dfPtsLock: boolean = false): any {
    if (order.isInvertedOrderFg == false) {
      return this.getBaseNdfTracking(order, false, true, dfPtsLock);
    }
    let ndfTracking = this.getInvertFwdTracking(order);
    if (ndfTracking == EMPTY_RATE_STRING) {
      return ndfTracking;
    }
    ndfTracking = new BigNumber(ndfTracking);
    let fwdPtsNdfTracking: BigNumber = this.getInvertNdfTrackingFwdPts(order, false, dfPtsLock);
    if (fwdPtsNdfTracking === null) {
      return EMPTY_RATE_STRING;
    }

    if (CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType) {
      if (fwdPtsNdfTracking !== null) {
        // might be struckthrough if no fwdPts, but apply all available
        ndfTracking = ndfTracking.plus(fwdPtsNdfTracking);
      }
    } else if (
      CubiqTS.RequestTrackingType.SPOT_RATE === order.requestTrackingType ||
      CubiqTS.Tenor.SPOT === order.settleTenor
    ) {
      if (fwdPtsNdfTracking !== null) {
        ndfTracking = ndfTracking.plus(fwdPtsNdfTracking);
      }
    }

    let ccyPair: any = this.getCcyPair(order);
    if (ccyPair) {
      let minPrecision: number = this.getMinInvertPrecision(ccyPair);
      let maxPrecision: number = this.getMaxInvertPrecision(ccyPair);
      return this.roundRateToPrecision(ndfTracking, minPrecision, maxPrecision);
    }
    return ndfTracking;
  }

  private getNonRoundedInvertNdfTracking(
    order: any,
    includePtsForSptFwds: boolean = false,
    dfPtsLock: boolean = false,
  ): any {
    let spotTracking = this.getBaseNdfTracking(order, includePtsForSptFwds, false, dfPtsLock);
    if (!spotTracking || spotTracking === EMPTY_RATE_STRING) {
      return EMPTY_RATE_STRING;
    }
    spotTracking = new CubiqTS.BigNumber(1).dividedBy(spotTracking);
    return spotTracking.toString();
  }

  public getInvertNdfTracking(
    order: any,
    includePtsForSptFwds: boolean = false,
    dfPtsLock: boolean = false,
  ): any {
    if (this.isMktOrder(order)) return null;

    let ndfTracking: CubiqTS.BigNumber | null = this.getMarkedUpRequestRt(order);
    if (!ndfTracking) {
      return EMPTY_RATE_STRING;
    }
    if (order.isInvertedOrderFg) {
      ndfTracking = ndfTracking;
    } else {
      ndfTracking = new BigNumber("1").dividedBy(ndfTracking);
    }

    let fwdPtsNdfTracking: BigNumber = this.getInvertNdfTrackingFwdPts(order, false, dfPtsLock);
    if (fwdPtsNdfTracking === null) {
      return EMPTY_RATE_STRING;
    }
    if (
      includePtsForSptFwds ||
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType
    ) {
      if (fwdPtsNdfTracking !== null) {
        // might be struckthrough if no fwdPts, but apply all available
        ndfTracking = ndfTracking.plus(fwdPtsNdfTracking);
      }
    } else if (
      CubiqTS.RequestTrackingType.SPOT_RATE === order.requestTrackingType ||
      CubiqTS.Tenor.SPOT === order.settleTenor
    ) {
      if (fwdPtsNdfTracking !== null) {
        ndfTracking = ndfTracking.plus(fwdPtsNdfTracking);
      }
    }

    let ccyPair: any = this.getCcyPair(order);
    if (ccyPair) {
      let minPrecision: number = this.getMinInvertPrecision(ccyPair);
      let maxPrecision: number = this.getMaxInvertPrecision(ccyPair);
      return this.roundRateToPrecision(ndfTracking, minPrecision, maxPrecision);
    }

    return ndfTracking.toString();
  }

  public getBlotterNdfTracking(order: any) {
    let dfPtsLock = true;
    let round = true;
    let includePtsForSptFwds = false;

    if (this.modeIsDealt) {
      return this.getDealtNdfTracking(order, dfPtsLock);
    }
    return this.getBaseNdfTracking(order, includePtsForSptFwds, round, dfPtsLock);
  }

  public getBlotterFwdTracking(order: any): string {
    if (this.modeIsDealt) return this.getDealtFwdTracking(order);

    return this.getBaseFwdTracking(order);
  }

  public getDealtFwdTracking(order: any): string {
    let fwdTracking = this.getMarkedUpRequestRt(order);
    if (!fwdTracking) {
      return EMPTY_RATE_STRING;
    }
    let ccyPair: any = this.getCcyPair(order);
    if (ccyPair) {
      let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
      let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);
      return this.roundRateToPrecision(fwdTracking, minPrecision, maxPrecision);
    }
    return fwdTracking.toString();
  }

  public getInvertFwdTracking(order: any, round: boolean = true): any {
    let fwdTracking;
    if (order.isInvertedOrderFg) {
      fwdTracking = this.getMarkedUpRequestRt(order);
    } else {
      fwdTracking = this.getBaseFwdTracking(order);
      if (fwdTracking != EMPTY_RATE_STRING && fwdTracking != "") {
        fwdTracking = new CubiqTS.BigNumber(1).dividedBy(this.getBaseFwdTracking(order));
      }
    }
    if (fwdTracking == null) {
      return EMPTY_RATE_STRING;
    }
    if (round) {
      let ccyPair: any = this.getCcyPair(order);
      if (ccyPair && fwdTracking != EMPTY_RATE_STRING && fwdTracking != "") {
        let minPrecision: number = this.getMinInvertPrecision(ccyPair);
        let maxPrecision: number = this.getMaxInvertPrecision(ccyPair);

        return this.roundRateToPrecision(fwdTracking, minPrecision, maxPrecision);
      }
    }
    return fwdTracking.toString();
  }

  public getBaseFwdTracking(order: any): any {
    let fwdTracking: any;

    if (order.isLadderAggregate) {
      // no fwd tracking rate is applicable
      if (order.isInvertedOrderFg) {
        return order.aggregationInvertTrackingRate;
      } else {
        return order.aggregationTrackingRate;
      }
    }

    let markedUpRequestRt = this.getMarkedUpRequestRt(order);
    if (!markedUpRequestRt) {
      return EMPTY_RATE_STRING;
    }

    if (order.isInvertedOrderFg) {
      fwdTracking = new BigNumber("1").dividedBy(markedUpRequestRt);
    } else {
      fwdTracking = markedUpRequestRt;
    }
    let ccyPair: any = this.getCcyPair(order);
    if (ccyPair) {
      let minPrecision: number = this.getMinPrecision(ccyPair, order, false);
      let maxPrecision: number = this.getMaxPrecision(ccyPair, order, false);
      return this.roundRateToPrecision(fwdTracking, minPrecision, maxPrecision);
    }

    return fwdTracking.toString();
  }

  public getEntryMarkup(order: CubiqTS.OrderDataImpl, showType: boolean = true): string {
    let ccyPair = this.getCcyPair(order);
    let markupType: CubiqTS.MarkUpType = order.spotMarkupType;
    let markupValue: CubiqTS.BigNumber = order.spotMarkupValue;
    if (
      CubiqTS.RequestOrderType.BMARK == order.requestedOrderType &&
      order.allocationData &&
      order.allocationData.length > 0
    ) {
      markupType = order.allocationData[0].markupType;
      markupValue = order.allocationData[0].markupQty;
    }

    if (
      markupValue == null ||
      (CubiqTS.BigNumber.isBigNumber(markupValue) &&
        markupValue.toString() === ZERO_RATE.toString())
    ) {
      return EMPTY_RATE_STRING;
    }

    // If it's decimal, we scale up to pips
    if (markupType == CubiqTS.MarkUpType.Decimal) {
      markupValue = markupValue.times(this.getDecsToPipsMultiplier(order));
      // We've upgraded our value to Pip precision
      markupType = CubiqTS.MarkUpType.Pips;
    }
    let precision: number = 2;
    if (markupType == CubiqTS.MarkUpType.Pips) {
      // We've scaled decimal to pips at this stage
      let minPrecision = this.getMinPrecision(ccyPair, order, false);
      let maxPrecision = this.getMaxMarkupPrecision(ccyPair, order, false);
      precision = maxPrecision - minPrecision;
    }
    if (markupType == CubiqTS.MarkUpType.Percentage) {
      precision = 4;
    }

    let type: string = this._translationService.instant(
      "MarkUpType." + CubiqTS.MarkUpType[markupType],
    );
    if (showType) {
      return markupValue.toFixed(precision) + " " + type;
    }
    return markupValue.toFixed(precision);
  }

  public getEntryAllocationMarkup(
    allocation: CubiqTS.AllocationDataImpl,
    showType: boolean = true,
    orderData: OrderDataImpl,
  ): IFormattedMarkup {
    let ccyPair: any = null;
    let inverted: boolean = orderData.isInvertedOrderFg;

    if (this.myCcyPairLookup && allocation != null) {
      ccyPair = this.myCcyPairLookup.getCcyPair(allocation.baseCcy, allocation.nonbaseCcy);
    }

    let markupType: CubiqTS.MarkUpType = allocation.markupType;
    let markupValue: CubiqTS.BigNumber = allocation.markupQty;

    if (markupValue == null || CubiqTS.MarkUpType[markupType] === undefined) {
      return { numValue: ZERO_RATE, textValue: EMPTY_RATE_STRING };
    }

    if (markupValue.toString() === ZERO_RATE.toString()) {
      return { numValue: ZERO_RATE, textValue: EMPTY_RATE_STRING };
    }
    // If it's decimal, we scale up to pips
    if (markupType == CubiqTS.MarkUpType.Decimal && ccyPair != null) {
      let pipPow = this.getMinPrecision(ccyPair, null, this.modeIsDealt, inverted);
      let pipSize = Math.pow(10, pipPow);

      markupValue = markupValue.times(pipSize);
      // We've upgraded our value to Pip precision
      markupType = CubiqTS.MarkUpType.Pips;
    }

    let precision: number = 2;
    if (markupType == CubiqTS.MarkUpType.Pips && ccyPair != null) {
      // We've scaled decimal to pips at this stage
      let minPrecision = this.getMinPrecision(ccyPair, null, false, inverted);
      let maxPrecision = this.getMaxMarkupPrecision(ccyPair, null, false, inverted);
      precision = maxPrecision - minPrecision;
    }

    if (markupType == CubiqTS.MarkUpType.Percentage) {
      precision = 4;
    }

    let type: string = this._translationService.instant(
      "MarkUpType." + CubiqTS.MarkUpType[markupType],
    );

    if (showType) {
      return { numValue: markupValue, textValue: markupValue.toFixed(precision) + " " + type };
    }

    return { numValue: markupValue, textValue: markupValue.toFixed(precision) };
  }

  public getDealtMarkup(order: any): any {
    return this.getDealtMarginFormattedDecs(order);
  }

  public getDealtAllocationMarkup(
    allocation: CubiqTS.AllocationDataImpl,
    order: OrderDataImpl,
  ): IFormattedMarkup {
    let ccyPair: any = null;
    if (this.myCcyPairLookup && allocation != null) {
      ccyPair = this.myCcyPairLookup.getCcyPair(allocation.baseCcy, allocation.nonbaseCcy);
    }

    let margin: CubiqTS.BigNumber | undefined | null = null;
    if (allocation != null) {
      let markup = allocation.salesMarkup;

      let spotMarkup: BigNumber = markup;
      let fwdMarkup: BigNumber = allocation.reqFwdPoints;
      let populated: boolean = false;

      if (null != allocation.fillDetails) {
        let mktAllIn: BigNumber = allocation.fillDetails.marketFillAllInRt;
        let clientAllIn: BigNumber = allocation.fillDetails.clientFillAllInRt;

        if (mktAllIn != null && clientAllIn != null) {
          if (this.isAllocationBuyingBase(order, allocation)) {
            markup = clientAllIn.minus(mktAllIn);
          } else {
            markup = mktAllIn.minus(clientAllIn);
          }

          populated = true;
        }
      }

      if (!populated && null != spotMarkup && null != fwdMarkup) {
        if (this.buyBase(order)) {
          markup = spotMarkup.plus(fwdMarkup);
        } else {
          markup = spotMarkup.minus(fwdMarkup);
        }
      }
      margin = markup;
    }

    if (!margin) {
      return { numValue: ZERO_RATE, textValue: EMPTY_RATE_STRING };
    }

    if (ZERO_RATE.isEqualTo(margin)) {
      if (allocation != null) {
        return this.formatAllocationMarkupWithPrecision(ZERO_RATE, ccyPair, allocation, order);
      } else {
        return { numValue: ZERO_RATE, textValue: "0" };
      }
    }

    return this.formatAllocationMarkupWithPrecision(margin, ccyPair, allocation, order);
  }

  public isAllocationBuyingBase(
    order: CubiqTS.OrderDataImpl,
    allocation: CubiqTS.AllocationDataImpl,
  ): boolean {
    let buysBase =
      (allocation.baseCcy === allocation.fixedCcy) ==
      (allocation.allocationIntent === OrderIntent.BUY);

    if (order.isInvertedOrderFg && allocation.allocationType != CubiqTS.AllocationType.COMPONENT) {
      buysBase = !buysBase;
    }

    return buysBase;
  }

  public getBaseMarkup(order: any): any {
    if (order.isInvertedOrderFg) {
      let markup: number = this.getDealtMargin(order, true);
      if (
        markup == null ||
        CubiqTS.OrderType.LIMIT_CALL == order.orderType ||
        CubiqTS.RequestOrderType.MARKET == order.requestedOrderType
      ) {
        // Determine if there is a better value than null to return
        return this.getUnresolvedDisplayMarkup(order, false);
      }
      if (markup == 0) {
        return "0";
      }

      let orderRequestRt = this.getOrderRequestRt(order);
      let requestRt: CubiqTS.BigNumber;

      if (orderRequestRt === EMPTY_RATE_STRING) orderRequestRt = null;

      if (CubiqTS.OrderType.ALGO === order.orderType && orderRequestRt == null) {
        // for markup calc use Inverted Market Rate for ALGO without request rate.
        let cachedRate = this.rateCache.getRateByKey(this.rateCache.getSpotRateKey(order));
        let midRate = cachedRate != null ? cachedRate.midRate : null;

        requestRt =
          midRate != null
            ? new CubiqTS.BigNumber(1).dividedBy(new CubiqTS.BigNumber(midRate))
            : null;
      } else {
        requestRt = orderRequestRt != null ? new CubiqTS.BigNumber(orderRequestRt) : null;
      }

      if (requestRt === null) return "0";

      let invTracking = requestRt.plus(markup);
      let tracking = new CubiqTS.BigNumber(1).dividedBy(invTracking);
      let invMarkup: CubiqTS.BigNumber;
      if (tracking.isGreaterThan(invTracking)) {
        invMarkup = tracking.minus(new CubiqTS.BigNumber(1).dividedBy(requestRt));
      } else {
        invMarkup = new CubiqTS.BigNumber(1).dividedBy(requestRt).minus(tracking);
      }
      invMarkup = invMarkup.absoluteValue();
      const ccyPair: any = this.getCcyPair(order);
      if (ccyPair && !invMarkup.isNaN()) {
        let minPrecision = this.getMinPrecision(ccyPair, order, false);
        let maxPrecision = this.getMaxMarkupPrecision(ccyPair, order, false);

        let currentDecs: number = invMarkup.decimalPlaces();

        if (currentDecs !== null) {
          if (currentDecs < minPrecision) {
            return invMarkup.toFixed(minPrecision);
          } else if (currentDecs > maxPrecision) {
            return invMarkup.toFixed(maxPrecision);
          } else {
            return invMarkup.toFixed(currentDecs);
          }
        }
        return invMarkup.toString();
      } else if (!invMarkup.isNaN()) {
        // not enough information to format, but keep value
        return invMarkup.toString();
      }
      return "0";
    }
    return this.getDealtMarkup(order);
  }

  public getInvertMarkup(order: any): any {
    if (order.isInvertedOrderFg) {
      return this.getDealtMarkup(order);
    } else {
      let invFwdTracking = this.getInvertFwdTracking(order, true);
      if (
        CubiqTS.OrderType.LIMIT_CALL == order.orderType ||
        CubiqTS.RequestOrderType.MARKET == order.requestedOrderType ||
        invFwdTracking == null ||
        invFwdTracking == EMPTY_RATE_STRING
      ) {
        return this.getUnresolvedDisplayMarkup(order, true);
      }

      let orderRequestRt = this.getOrderRequestRt(order);
      let requestRt: CubiqTS.BigNumber;

      if (orderRequestRt === EMPTY_RATE_STRING) orderRequestRt = null;

      if (CubiqTS.OrderType.ALGO === order.orderType && orderRequestRt == null) {
        // for markup calc use Inverted Market Rate for ALGO without request rate.
        let cachedRate = this.rateCache.getRateByKey(this.rateCache.getSpotRateKey(order));
        let midRate = cachedRate != null ? cachedRate.midRate : null;

        requestRt =
          midRate != null
            ? new CubiqTS.BigNumber(1).dividedBy(new CubiqTS.BigNumber(midRate))
            : null;
      } else {
        requestRt =
          orderRequestRt != null ? new CubiqTS.BigNumber(1).dividedBy(orderRequestRt) : null;
      }

      if (requestRt === null) {
        return "0";
      } else {
        const ccyPair: any = this.getCcyPair(order);
        if (ccyPair && !requestRt.isNaN()) {
          let minPrecision = this.getMinInvertPrecision(ccyPair);
          let maxPrecision = this.getMaxInvertPrecision(ccyPair);

          let currentDecs: number = requestRt.decimalPlaces();

          if (currentDecs !== null) {
            if (currentDecs < minPrecision) {
              requestRt = new CubiqTS.BigNumber(requestRt.toFixed(minPrecision));
            } else if (currentDecs > maxPrecision) {
              requestRt = new CubiqTS.BigNumber(requestRt.toFixed(maxPrecision));
            } else {
              requestRt = new CubiqTS.BigNumber(requestRt.toFixed(currentDecs));
            }
          }
        }
      }

      let invMarkup: CubiqTS.BigNumber;
      if (requestRt.isGreaterThan(invFwdTracking)) {
        invMarkup = new CubiqTS.BigNumber(invFwdTracking).minus(requestRt);
      } else {
        invMarkup = requestRt.minus(invFwdTracking);
      }
      invMarkup = invMarkup.absoluteValue();
      if (!invMarkup.isEqualTo(0)) {
        const ccyPair: any = this.getCcyPair(order);
        if (ccyPair && !invMarkup.isNaN()) {
          let minPrecision = this.getMinInvertPrecision(ccyPair);
          let maxPrecision = ccyPair.markupSptInvDecimals;

          let currentDecs: number = invMarkup.decimalPlaces();

          if (currentDecs !== null) {
            if (currentDecs < minPrecision) {
              return invMarkup.toFixed(minPrecision);
            } else if (currentDecs > maxPrecision) {
              return invMarkup.toFixed(maxPrecision);
            } else {
              return invMarkup.toFixed(currentDecs);
            }
          }
          return invMarkup.toString();
        } else if (!invMarkup.isNaN()) {
          // not enough information to format, but keep value
          return invMarkup.toString();
        }
      }
      return "0";
    }
  }

  /* applicable only to TSL orders */
  public getTslOrderOrigTrackingRt(order: any): any {
    if (
      order.trailingStopDetails == Object(order.trailingStopDetails) &&
      order.trailingStopDetails instanceof CubiqTS.TrailingStopDetailsImpl &&
      order.trailingStopDetails.origTrackingRt ==
        Object(order.trailingStopDetails.origTrackingRt) &&
      order.trailingStopDetails.origTrackingRt instanceof CubiqTS.BigNumber
    ) {
      let origTrackingRt = order.trailingStopDetails.origTrackingRt;

      let ccyPair: any = this.getCcyPair(order);

      if (ccyPair) {
        let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
        let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);
        return this.roundRateToPrecision(origTrackingRt, minPrecision, maxPrecision);
      }

      origTrackingRt.toString();
    }

    return "";
  }

  roundRateToPrecision(rate: any, minPrecision: number, maxPrecision: number): string {
    // rate needs to be BigNumber
    //  may need to add in second method to do pip styling
    let bigRate: BigNumber = new BigNumber(rate);
    let fromDecs: number = bigRate.decimalPlaces();
    let toDecs: number = fromDecs;

    if (fromDecs !== null) {
      if (fromDecs < minPrecision) {
        toDecs = minPrecision;
      } else if (fromDecs > maxPrecision) {
        toDecs = maxPrecision;
      } else {
        toDecs = fromDecs;
      }
    }

    let rounded: string = bigRate.toFixed(toDecs);
    return rounded;
  }

  /* Private methods for lookups and calculations */
  private getMarkedUpRequestRt(order: any): any {
    if (order.markedUpRequestRate != null) {
      return order.markedUpRequestRate;
    }

    if (order.requestRt != undefined && order.requestRt != null) {
      let markedUpRequestRt = new BigNumber(order.requestRt);
      let margin: number = this.getDealtMargin(order);
      if (margin == null) {
        margin = 0;
      }
      return (order.markedUpRequestRate = markedUpRequestRt.plus(margin));
    }

    return null;
  }

  private getDealtMargin(order: any, ignoreREfg = false): any {
    // Work out margin using the reverseenginerring flag
    // If reverseEng is true, then the margin is applied to
    // the request rate to get the tracking rate otherwise no margin is used
    if (order.isRateReverseEngineeringFg || ignoreREfg) {
      // Work out the absolute margin value
      let margin: number = isNaN(order.salesMargin) ? "" : order.salesMargin;
      // make negative if we are to subtract margin
      if (
        (!order.isInvertedOrderFg &&
          CubiqTS.OrderIntent.BUY === order.clientIntent &&
          order.baseCode == order.fixedCode) ||
        (!order.isInvertedOrderFg &&
          CubiqTS.OrderIntent.SELL === order.clientIntent &&
          order.nonBaseCode == order.fixedCode) ||
        (order.isInvertedOrderFg &&
          CubiqTS.OrderIntent.BUY === order.clientIntent &&
          order.nonBaseCode == order.fixedCode) ||
        (order.isInvertedOrderFg &&
          CubiqTS.OrderIntent.SELL === order.clientIntent &&
          order.baseCode == order.fixedCode)
      ) {
        margin = margin * -1;
      }
      return margin;
    }
    return 0;
  }

  /**
   * Get the string representation of markup for an order that has entry markup but the actual fill markup
   * has yet to be resolved - currently MKT orders and unfilled algos
   * @param order
   * @param inverted
   * @returns
   */
  private getUnresolvedDisplayMarkup(order: OrderDataImpl, inverted: boolean): string {
    let markup: string = "";
    switch (order.requestedOrderType) {
      case CubiqTS.RequestOrderType.ALGO:
        markup = this.getEntryMarkup(order, true);
        break;
      case CubiqTS.RequestOrderType.MARKET:
        const markupType: CubiqTS.MarkUpType = order.spotMarkupType;
        const markupValue: CubiqTS.BigNumber = order.spotMarkupValue;
        if (
          markupType == CubiqTS.MarkUpType.Pips &&
          (markupValue == null ||
            (CubiqTS.BigNumber.isBigNumber(markupValue) &&
              markupValue.toString() === ZERO_RATE.toString()))
        ) {
          markup = "0";
        } else {
          markup = this.getEntryMarkup(order, true);
        }
        break;
      default:
        break;
    }
    return markup;
  }

  /* match order detail screen logic don't use for calculations */
  getDealtMarginFormattedDecs(order: OrderDataImpl): string {
    const ccyPair: any = this.getCcyPair(order);
    let margin: CubiqTS.BigNumber | undefined | null = this.getSalesMargin(order);

    if (
      margin == null ||
      CubiqTS.OrderType.LIMIT_CALL == order.orderType ||
      CubiqTS.RequestOrderType.MARKET == order.requestedOrderType
    ) {
      // Determine if there is a better value than null to return
      return this.getUnresolvedDisplayMarkup(order, false);
    }

    if (ZERO_RATE.isEqualTo(margin)) {
      if (
        order != null &&
        (order.orderType == CubiqTS.OrderType.BENCHMARK ||
          order.orderType == CubiqTS.OrderType.BMALGO)
      ) {
        return this.formatWithPrecision(ZERO_RATE, ccyPair, order);
      } else {
        return "0";
      }
    }

    // margin = new BigNumber(this.getSalesMargin(order));

    // margin =  margin.absoluteValue();

    return this.formatWithPrecision(margin, ccyPair, order);
  }

  private formatWithPrecision(
    margin: CubiqTS.BigNumber,
    ccyPair: CubiqTS.CurrencyPairImpl,
    order: OrderDataImpl,
  ): string {
    if (ccyPair) {
      let minPrecision = this.getMinPrecision(ccyPair, order, true);
      let maxPrecision = this.getMaxMarkupPrecision(ccyPair, order, true);

      let currentDecs: number = margin.decimalPlaces();

      if (currentDecs !== null) {
        if (currentDecs < minPrecision) {
          return margin.toFixed(minPrecision);
        } else if (currentDecs > maxPrecision) {
          return margin.toFixed(maxPrecision);
        } else {
          return margin.toFixed(currentDecs);
        }
      }

      return margin.toString();
    } else if (!margin.isNaN()) {
      // not enough information to format, but keep value
      return margin.toString();
    }

    return "0";
  }

  private formatAllocationMarkupWithPrecision(
    margin: CubiqTS.BigNumber,
    ccyPair: CubiqTS.CurrencyPairImpl,
    allocation: CubiqTS.AllocationDataImpl,
    order: OrderDataImpl,
  ): IFormattedMarkup {
    let numValue: BigNumber = margin;
    let textValue: string;
    if (ccyPair) {
      let inverted: boolean = order.isInvertedOrderFg;
      let minPrecision = this.getMinPrecision(ccyPair, null, true, inverted);
      let maxPrecision = this.getMaxMarkupPrecision(ccyPair, null, true, inverted);

      let currentDecs: number = margin.decimalPlaces();

      if (currentDecs !== null) {
        if (currentDecs < minPrecision) {
          textValue = margin.toFixed(minPrecision);
        } else if (currentDecs > maxPrecision) {
          textValue = margin.toFixed(maxPrecision);
        } else {
          textValue = margin.toFixed(currentDecs);
        }
      } else {
        textValue = margin.toString();
      }
    } else if (!margin.isNaN()) {
      // not enough information to format, but keep value
      textValue = margin.toString();
    } else {
      numValue = ZERO;
      textValue = "0";
    }

    return { numValue, textValue };
  }

  private getSalesMargin(orderData: OrderDataImpl): CubiqTS.BigNumber {
    let ret: CubiqTS.BigNumber = orderData.salesMargin;

    let isBmark: boolean =
      CubiqTS.RequestOrderType.BMARK === orderData.requestedOrderType ||
      CubiqTS.RequestOrderType.BMALGO === orderData.requestedOrderType;

    if (isBmark) {
      if (orderData.allocationData != null && orderData.allocationData.length > 0) {
        let markup = orderData.allocationData[0].salesMarkup;

        let spotMarkup: BigNumber = orderData.allocationData[0].salesMarkup;
        let fwdMarkup: BigNumber = orderData.allocationData[0].reqFwdPoints;
        if (null != orderData.allocationData[0].fillDetails) {
          let fillMktPts: BigNumber = orderData.allocationData[0].fillDetails.marketFillPts;
          let fillClientPts: BigNumber = orderData.allocationData[0].fillDetails.clientFillPts;
          if (null != fillMktPts && null != fillClientPts) {
            fwdMarkup = fillClientPts.minus(fillMktPts);
          }
        }

        if (null != spotMarkup && null != fwdMarkup) {
          if (this.clientBuyingBase(orderData)) {
            markup = spotMarkup.plus(fwdMarkup);
          } else {
            markup = spotMarkup.minus(fwdMarkup);
          }
        }

        ret = markup;
      } else {
        ret = null;
      }
    } else if (orderData.orderType === CubiqTS.OrderType.ALGO) {
      let spotMarkup: BigNumber = orderData.salesMargin;
      let markup: BigNumber = spotMarkup;

      if (orderData.lastFillDetails != null) {
        let marketAllIn: BigNumber = orderData.lastFillDetails.marketFillAllInRt;
        let clientAllIn: BigNumber = orderData.lastFillDetails.clientFillAllInRt;

        if (null != marketAllIn && null != clientAllIn) {
          if (this.clientBuyingBase(orderData)) {
            markup = clientAllIn.minus(marketAllIn);
          } else {
            markup = marketAllIn.minus(clientAllIn);
          }
        }
      }

      ret = markup;
    }

    return ret;
  }

  private getCcyPair(order: any): CubiqTS.CurrencyPairImpl {
    if (this.myCcyPairLookup) {
      return this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
    }
    return null;
  }

  private getFwdPipsQty(ccyPair: any, order: any, dealt: boolean): number {
    if (order.isInvertedOrderFg && dealt) {
      return ccyPair.fwdInvertPipsQty;
    } else {
      return ccyPair.fwdPipsQty;
    }
  }

  private getFwdPipsPrecision(ccyPair: any, order: any, dealt: boolean): number {
    // let fwdPipsPrecision = ccyPair.fwdInvertDecimalsQty - ccyPair.fwdD
    if (order.isInvertedOrderFg && dealt) {
      return ccyPair.fwdInvertDecimalsQty - ccyPair.fwdInvertPipsQty;
    } else {
      return ccyPair.fwdRateDecimalsQty - ccyPair.fwdPipsQty;
    }
  }

  private getMinPrecision(
    ccyPair: any,
    order: any,
    dealt: boolean,
    inverted: boolean = false,
  ): number {
    let invertedFg = inverted;

    if (order != null) {
      invertedFg = order.isInvertedOrderFg;
    }

    if (invertedFg && dealt) {
      return ccyPair.spotInvertDecimalsQty;
    } else {
      return ccyPair.pipsQty;
    }
  }

  private getMaxPrecision(
    ccyPair: any,
    order: any,
    dealt: boolean,
    inverted: boolean = false,
  ): number {
    let invertedFg = inverted;

    if (order != null) {
      invertedFg = order.isInvertedOrderFg;
    }

    if (invertedFg && dealt) {
      return ccyPair.fwdInvertDecimalsQty;
    } else {
      return ccyPair.fwdRateDecimalsQty;
    }
  }

  private getMinInvertPrecision(ccyPair: any): number {
    return ccyPair.spotInvertDecimalsQty;
  }

  private getMaxInvertPrecision(ccyPair: any): number {
    return ccyPair.fwdInvertDecimalsQty;
  }

  private getMaxMarkupPrecision(
    ccyPair: any,
    order: any,
    dealt: boolean,
    inverted: boolean = false,
  ): number {
    let invertedFg = inverted;

    if (order != null) {
      invertedFg = order.isInvertedOrderFg;
    }

    if (invertedFg && dealt) {
      return ccyPair.markupSptInvDecimals;
    } else {
      return ccyPair.markupSptDecimals;
    }
  }

  public getRoundedValue(order: any, value: number): string {
    let ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
    let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
    let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);
    return this.roundRateToPrecision(value, minPrecision, maxPrecision);
  }

  protected getRoundedMidRate(order: any, rate: Rate): string {
    let ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
    let minPrecision: number = this.getMinPrecision(ccyPair, order, true);
    let maxPrecision: number = this.getMaxPrecision(ccyPair, order, true);
    return this.roundRateToPrecision(rate.midRate, minPrecision, maxPrecision);
  }

  public getBaseFwdPts(order: any, dfPtsLock: boolean = false) {
    let rate: Rate =
      dfPtsLock && this.rateCache.hasDetailFillFwdRate(order)
        ? this.rateCache.getDetailFillFwdRate(order)
        : this.rateCache.getRateByKey(this.rateCache.getFwdRateKey(order));

    let bankBuysBase: boolean =
      (order.baseCode == order.fixedCode && order.clientIntent == OrderIntent.SELL) ||
      (order.clientIntent == OrderIntent.BUY && order.nonBaseCode == order.fixedCode);
    let fwdPts;

    if (rate != null) {
      if (CubiqTS.RequestOrderType.CALL === order.requestedOrderType) {
        fwdPts = new BigNumber(this.getRoundedMidRate(order, rate));
      } else fwdPts = new BigNumber(bankBuysBase ? rate.bidRate : rate.offerRate);

      return fwdPts;
    }

    return null;
  }

  public getDealtFwdPts(order: any, dfPtsLock: boolean = false): any {
    if (this.modeIsDealt && order.isInvertedOrderFg) {
      let spotTracking = this.getDealtSpotTracking(order, true, dfPtsLock);
      let fwdTracking = this.getDealtFwdTracking(order);
      if (spotTracking == EMPTY_RATE_STRING || fwdTracking == EMPTY_RATE_STRING) {
        return null;
      }
      return new CubiqTS.BigNumber(spotTracking).minus(fwdTracking).times(-1);
    }
    return this.getBaseFwdPts(order, dfPtsLock);
  }

  public getInvertFwdPts(order: any, dfPtsLock: boolean = false): any {
    let spotTracking = this.getNonRoundedInvertSpotTracking(order, true, dfPtsLock);
    let fwdTracking = this.getInvertFwdTracking(order, false);
    if (spotTracking == EMPTY_RATE_STRING || fwdTracking == EMPTY_RATE_STRING) {
      return null;
    }
    return new CubiqTS.BigNumber(spotTracking)
      .minus(fwdTracking)
      .times(CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType ? -1 : 1);
  }

  public getBaseNdfTrackingFwdPts(
    order: any,
    dfPtsLock: boolean = false,
  ): CubiqTS.BigNumber | null {
    let rate: Rate =
      dfPtsLock && this.rateCache.hasDetailFillNdfRate(order)
        ? this.rateCache.getDetailFillNdfRate(order)
        : this.rateCache.getRateByKey(this.rateCache.getFwdRateNdfTrackingKey(order));
    let bankBuysBase: boolean =
      (order.baseCode == order.fixedCode && order.clientIntent == OrderIntent.SELL) ||
      (order.clientIntent == OrderIntent.BUY && order.nonBaseCode == order.fixedCode);
    let ndfTrackingFwdPts: CubiqTS.BigNumber;

    if (rate != null) {
      if (CubiqTS.RequestOrderType.CALL === order.requestedOrderType)
        ndfTrackingFwdPts = new BigNumber(this.getRoundedMidRate(order, rate));
      else ndfTrackingFwdPts = new BigNumber(bankBuysBase ? rate.bidRate : rate.offerRate);

      return ndfTrackingFwdPts;
    }

    return null;
  }

  public getInvertNdfTrackingFwdPts(
    order: any,
    includePtsForSptFwds: boolean = true,
    dfPtsLock: boolean = false,
  ): BigNumber | null {
    let invertNdfTracking = this.getNonRoundedInvertNdfTracking(
      order,
      includePtsForSptFwds,
      dfPtsLock,
    );
    let invertFwdTracking = this.getInvertFwdTracking(order, false);

    if (
      !invertNdfTracking ||
      invertNdfTracking === EMPTY_RATE_STRING ||
      !invertFwdTracking ||
      invertFwdTracking === EMPTY_RATE_STRING
    ) {
      return null;
    }
    return new CubiqTS.BigNumber(invertNdfTracking).minus(invertFwdTracking);
  }

  public getBaseFwdPtsInNdfTrackingMode(order: any, dfPtsLock: boolean = false) {
    let fwdRate: Rate =
      dfPtsLock && this.rateCache.hasDetailFillFwdRate(order)
        ? this.rateCache.getDetailFillFwdRate(order)
        : this.rateCache.getRateByKey(this.rateCache.getFwdRateKey(order));
    let ndfTrackingFwdRate =
      dfPtsLock && this.rateCache.hasDetailFillNdfRate(order)
        ? this.rateCache.getDetailFillNdfRate(order)
        : this.rateCache.getRateByKey(this.rateCache.getFwdRateNdfTrackingKey(order));
    let bankBuysBase: boolean =
      (order.baseCode == order.fixedCode && order.clientIntent == OrderIntent.SELL) ||
      (order.clientIntent == OrderIntent.BUY && order.nonBaseCode == order.fixedCode);
    let fwdPts: BigNumber;
    let ndfTrackingFwdPts: BigNumber;
    if (ndfTrackingFwdRate == null) {
      ndfTrackingFwdRate = new Rate();
      ndfTrackingFwdRate.bidRate = "0";
      ndfTrackingFwdRate.offerRate = "0";
      ndfTrackingFwdRate.midRate = "0";
    }
    if ((fwdRate != null || this.isSpotOrder(order)) && ndfTrackingFwdRate != null) {
      if (CubiqTS.RequestOrderType.CALL === order.requestedOrderType) {
        fwdPts = this.isSpotOrder(order)
          ? ZERO_RATE
          : new BigNumber(this.getRoundedMidRate(order, fwdRate));
        ndfTrackingFwdPts = new BigNumber(this.getRoundedMidRate(order, ndfTrackingFwdRate));
      } else {
        fwdPts = this.isSpotOrder(order)
          ? ZERO_RATE
          : new BigNumber(bankBuysBase ? fwdRate.bidRate : fwdRate.offerRate);
        ndfTrackingFwdPts = new BigNumber(
          bankBuysBase ? ndfTrackingFwdRate.bidRate : ndfTrackingFwdRate.offerRate,
        );
      }

      return fwdPts.minus(ndfTrackingFwdPts);
    }

    return null;
  }

  public getDealtFwdPtsInNdfTrackingMode(order: any, dfPtsLock: boolean = false): any {
    if (order.isInvertedOrderFg && this.modeIsDealt) {
      let invertNdfTrackingFwdPts = this.getInvertNdfTrackingFwdPts(order, false, dfPtsLock);
      if (invertNdfTrackingFwdPts == null) {
        return null;
      }
      return invertNdfTrackingFwdPts.times(-1);
    }
    return this.getBaseFwdPtsInNdfTrackingMode(order, dfPtsLock);
  }

  public getMidFwdPts(order: any) {
    let rate: Rate = this.rateCache.getRateByKey(this.rateCache.getFwdRateKey(order));
    if (rate != null) {
      return rate.midRate;
    }

    return null;
  }

  public getInvertFwdPtsFormattedDecs(order: any): string | null {
    let dfPtsLock = true;
    let fwdPts = this.getInvertFwdPts(order, dfPtsLock);

    if (fwdPts) {
      let ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
      let fwdPipsQty = ccyPair.fwdInvertPipsQty;
      let fwdPipsMultipler = Math.pow(10, fwdPipsQty);
      let fwdPipsPrecision = ccyPair.fwdInvertDecimalsQty - ccyPair.fwdInvertPipsQty;
      return fwdPts.times(fwdPipsMultipler).toFixed(fwdPipsPrecision);
    }
    return null;
  }

  public getFwdPtsFormattedDecs(order: any): string | null {
    if (this.isMktOrder(order)) return null;

    let dfPtsLock = true;
    let fwdPts = this.getDealtFwdPts(order, dfPtsLock);

    if (fwdPts == null) {
      if (order.forwardDetails) fwdPts = order.forwardDetails.currentMktPts;
      if (null == fwdPts) {
        return null;
      }
    }

    let ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
    let fwdPipsQty = this.getFwdPipsQty(ccyPair, order, this.modeIsDealt);
    let fwdPipsMultipler = Math.pow(10, fwdPipsQty);
    let fwdPipsPrecision = this.getFwdPipsPrecision(ccyPair, order, this.modeIsDealt);
    return fwdPts.times(fwdPipsMultipler).toFixed(fwdPipsPrecision);
  }

  getFwdPtsNdfTrackingFormattedDecs(order: any) {
    if (this.isMktOrder(order)) return null;

    let dfPtsLock = true;
    let fwdPts =
      this.modeIsDealt && order.isInvertedOrderFg
        ? this.getDealtFwdPtsInNdfTrackingMode(order, dfPtsLock)
        : this.getBaseFwdPtsInNdfTrackingMode(order, dfPtsLock);

    if (fwdPts === null) {
      if (order.forwardDetails) fwdPts = order.forwardDetails.currentMktPts;
      if (null == fwdPts) {
        return null;
      }
    }

    let ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
    let fwdPipsQty = this.getFwdPipsQty(ccyPair, order, this.modeIsDealt);
    let fwdPipsMultipler = Math.pow(10, fwdPipsQty);
    let fwdPipsPrecision = this.getFwdPipsPrecision(ccyPair, order, this.modeIsDealt);
    return fwdPts.times(fwdPipsMultipler).toFixed(fwdPipsPrecision);
  }

  getInvertFwdPtsNdfTrackingFormattedDecs(order: any): string {
    if (this.isMktOrder(order)) return null;

    let dfPtsLock = true;
    let fwdPts = this.getInvertNdfTrackingFwdPts(order, true, dfPtsLock);
    if (fwdPts === null) {
      return EMPTY_RATE_STRING;
    }
    let ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
    let fwdPipsQty = ccyPair.fwdInvertPipsQty;
    let fwdPipsMultipler = Math.pow(10, fwdPipsQty);
    let fwdPipsPrecision = ccyPair.fwdInvertDecimalsQty - ccyPair.fwdInvertPipsQty;

    return fwdPts.times(fwdPipsMultipler).times(-1).toFixed(fwdPipsPrecision);
  }

  getDecsToPipsMultiplier(order: CubiqTS.OrderDataImpl): number {
    let ccyPair: CubiqTS.CurrencyPairImpl = this.myCcyPairLookup.getCcyPair(
      order.baseCode,
      order.nonBaseCode,
    );
    let pipPow = this.getMinPrecision(ccyPair, order, this.modeIsDealt);
    let pipSize = Math.pow(10, pipPow);
    return pipSize;
  }

  isStrikethroughSpotTracking(order: CubiqTS.OrderDataImpl): boolean {
    return (
      !this.isMarketOrder(order) &&
      !this.isSpotOrder(order) &&
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType &&
      !this.isFwdRateTradeable(order)
    );
  }

  isStrikethroughNdfTracking(order: CubiqTS.OrderDataImpl): boolean {
    if (this.isMarketOrder(order)) {
      return false;
    } else if (
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType &&
      !this.isSpotOrder(order)
    ) {
      return !this.isFwdRateTradeable(order) || !this.isFwdRateNdfTrackingTradeable(order);
    } else if (
      CubiqTS.RequestTrackingType.SPOT_RATE === order.requestTrackingType &&
      !this.isSpotOrder(order)
    ) {
      return !this.isFwdRateNdfTrackingTradeable(order);
    } else if (this.isSpotOrder(order)) {
      return !this.isFwdRateNdfTrackingTradeable(order);
    }

    return false;
  }

  isStrikethroughMarketMid(order: CubiqTS.OrderDataImpl): boolean {
    return (
      !this.isSpotRateTradeable(order) ||
      (CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType &&
        !this.isFwdRateTradeable(order) &&
        !this.isSpotOrder(order))
    );
  }

  isStrikethroughFwdTracking(order: CubiqTS.OrderDataImpl): boolean {
    return false;
  }

  isStrikethroughFwdPts(order: CubiqTS.OrderDataImpl): boolean {
    return (
      !this.isMarketOrder(order) &&
      this.isFwdRateApplicable(order) &&
      !this.isFwdRateTradeable(order)
    );
  }

  isStrikethroughFwdPtsNdfTracking(order: any): boolean {
    if (this.isMarketOrder(order)) {
      return false;
    } else if (CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType) {
      return !this.isFwdRateTradeable(order) || !this.isFwdRateNdfTrackingTradeable(order);
    } else if (CubiqTS.RequestTrackingType.SPOT_RATE === order.requestTrackingType) {
      return !this.isFwdRateNdfTrackingTradeable(order);
    } else if (this.isSpotOrder(order)) {
      return !this.isFwdRateNdfTrackingTradeable(order);
    }

    return false;
  }

  isStrikethroughPercentAway(order: CubiqTS.OrderDataImpl): boolean {
    return (
      !this.isMarketOrder(order) &&
      (!this.isSpotRateTradeable(order) ||
        (this.isFwdRateApplicable(order) &&
          CubiqTS.RequestTrackingType.OUTRIGHT_RATE === order.requestTrackingType &&
          !this.isFwdRateTradeable(order)))
    );
  }

  isTempPips(order: any): boolean {
    let ccyPair: CubiqTS.CurrencyPairImpl;
    let userTemp: any = this.userTemps.getUserTemp(order.baseCode, order.nonBaseCode);

    if (userTemp == Object(userTemp)) {
      // structure is implictly from BfxOrderBlotterSettingsTemperatureComponent.temperatureTypes
      return CubiqTS.UnitType.PIPS === userTemp.temperatureTypeId;
    }

    ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);

    return CubiqTS.UnitType.PIPS === ccyPair.temperatureTypeId;
  }

  isTempPercents(order: any): boolean {
    let ccyPair: CubiqTS.CurrencyPairImpl;
    let userTemp: any = this.userTemps.getUserTemp(order.baseCode, order.nonBaseCode);

    if (userTemp == Object(userTemp)) {
      // structure is implictly from BfxOrderBlotterSettingsTemperatureComponent.temperatureTypes
      return CubiqTS.UnitType.PERCENT === userTemp.temperatureTypeId;
    }

    ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);

    return CubiqTS.UnitType.PERCENT === ccyPair.temperatureTypeId;
  }

  getTempPipSize(order: any): number {
    if (this.isTempPips(order)) {
      let ccyPair: CubiqTS.CurrencyPairImpl = this.myCcyPairLookup.getCcyPair(
        order.baseCode,
        order.nonBaseCode,
      );
      let pipPow = this.getMinPrecision(ccyPair, order, this.modeIsDealt) * -1;
      let pipSize = Math.pow(10, pipPow);
      return pipSize;
    }

    return NaN;
  }

  getTempWarmPipsAway(order: any): number {
    let ccyPair: CubiqTS.CurrencyPairImpl;
    let userTemp: any = this.userTemps.getUserTemp(order.baseCode, order.nonBaseCode);

    if (userTemp == Object(userTemp)) {
      if (CubiqTS.UnitType.PIPS === userTemp.temperatureTypeId) {
        return Number(userTemp.warmSetting);
      }

      return NaN;
    }

    ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);

    if (CubiqTS.UnitType.PIPS === ccyPair.temperatureTypeId) {
      return ccyPair.orderTempWarmValue.toNumber();
    }

    return NaN;
  }

  getTempHotPipsAway(order: any): number {
    let ccyPair: CubiqTS.CurrencyPairImpl;
    let userTemp: any = this.userTemps.getUserTemp(order.baseCode, order.nonBaseCode);

    if (userTemp == Object(userTemp)) {
      if (CubiqTS.UnitType.PIPS === userTemp.temperatureTypeId) {
        return Number(userTemp.hotSetting);
      }

      return NaN;
    }

    ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);

    if (CubiqTS.UnitType.PIPS === ccyPair.temperatureTypeId) {
      return Number(ccyPair.orderTempHotValue);
    }

    return NaN;
  }

  getTempWarmPercentAway(order: any): number {
    let ccyPair: CubiqTS.CurrencyPairImpl;
    let userTemp: any = this.userTemps.getUserTemp(order.baseCode, order.nonBaseCode);

    if (userTemp == Object(userTemp)) {
      if (CubiqTS.UnitType.PERCENT === userTemp.temperatureTypeId) {
        return Number(userTemp.warmSetting);
      }

      return NaN;
    }

    ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);

    if (CubiqTS.UnitType.PERCENT === ccyPair.temperatureTypeId) {
      return ccyPair.orderTempWarmValue.toNumber();
    }

    return NaN;
  }

  getTempHotPercentAway(order: any): number {
    let ccyPair: CubiqTS.CurrencyPairImpl;
    let userTemp: any = this.userTemps.getUserTemp(order.baseCode, order.nonBaseCode);

    if (userTemp == Object(userTemp)) {
      if (CubiqTS.UnitType.PERCENT === userTemp.temperatureTypeId) {
        return Number(userTemp.hotSetting);
      }

      return NaN;
    }

    ccyPair = this.myCcyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
    if (CubiqTS.UnitType.PERCENT === ccyPair.temperatureTypeId) {
      return Number(ccyPair.orderTempHotValue);
    }

    return NaN;
  }

  getBlotterTrackingSide(order: CubiqTS.OrderDataImpl): CubiqTS.RateSideType {
    let trackingSide: CubiqTS.RateSideType = this.getTrackingSide(order);
    return trackingSide;
  }

  getTrailingPips(order: CubiqTS.OrderDataImpl): BigNumber | null {
    let tsd = order != null ? (order.trailingStopDetails as CubiqTS.TrailingStopDetailsImpl) : null;

    if (tsd != null) {
      let multiplier = this.getDecsToPipsMultiplier(order);
      return tsd.trailingDistancePts.times(multiplier);
    }
    return null;
  }

  /**
   * Determine the number of additional places for displaying of Spot pip precision
   * @param ccyPair
   * @param invertFg
   * @returns
   */
  public getPairSpotPipPrecision(ccyPair: CubiqTS.CurrencyPair, invertFg: boolean = false): number {
    if (!ccyPair) {
      return 0;
    }
    const rateDecimals: number = invertFg ? ccyPair.spotInvertDecimalsQty : ccyPair.rateDecimalsQty;
    const entryDecimals: number = invertFg
      ? ccyPair.limitEntrySpotInvertDecimals
      : ccyPair.limitEntrySpotDecimals;
    const precision: CubiqTS.Precision = invertFg
      ? ccyPair.spotInvertPrecision
      : ccyPair.spotPrecision;

    let additional: number;
    switch (precision) {
      case CubiqTS.Precision.HALF:
      case CubiqTS.Precision.TENTH:
        additional = 1;
        break;
      default:
        additional = 0;
        break;
    }
    return entryDecimals - rateDecimals + additional;
  }

  /**
   * Get the trailing pips value for Blotter in current blotter mode
   * @param order
   * @returns
   */
  getBlotterTrailingPips(order: CubiqTS.OrderDataImpl): string {
    const isBase = !order.isInvertedOrderFg || !this.modeIsDealt;
    return isBase ? this.getBaseTrailingPips(order) : this.getInvertTrailingPips(order);
    return EMPTY_RATE_STRING;
  }

  /**
   * Get base trailing trigger of order independent of display mode
   * @param order
   * @returns
   */
  public getBaseTrailingPips(order: CubiqTS.OrderDataImpl): string {
    return this.getBaseInvertTrailingPips(order, order.isInvertedOrderFg, false);
  }

  public getInvertTrailingPips(order: CubiqTS.OrderDataImpl): string {
    return this.getBaseInvertTrailingPips(order, !order.isInvertedOrderFg, true);
  }

  /**
   * common code for both base and invert
   * @param order Main order with TrailingStopDetails
   * @param invertTsd Should the TSD rates be inverted? Selects trigger rate
   * @param invertRates Is the output pair an inverted rate? Selects precision
   * @returns
   */
  private getBaseInvertTrailingPips(
    order: CubiqTS.OrderDataImpl,
    invertTsd: boolean,
    invertRates: boolean,
  ): string {
    let tsd = order != null ? (order.trailingStopDetails as CubiqTS.TrailingStopDetailsImpl) : null;

    let pips: BigNumber;
    let pipPrecision: number = 0;
    if (tsd != null) {
      let ccyPair: CubiqTS.CurrencyPairImpl = this.getCcyPair(order);
      if (ccyPair) {
        let minPrecision: number = invertRates ? ccyPair.spotInvertDecimalsQty : ccyPair.pipsQty;
        pipPrecision = this.getPairSpotPipPrecision(ccyPair, invertRates);

        const multiplier = new BigNumber(`1e${minPrecision}`);
        if (!invertTsd) {
          pips =
            tsd.trailingDistancePts instanceof CubiqTS.BigNumber
              ? tsd.trailingDistancePts.times(multiplier)
              : null;
        } else {
          // We're in Base mode but with an inverted order
          let markedUpRequestRt = this.getMarkedUpRequestRt(order);
          if (!(markedUpRequestRt instanceof BigNumber)) {
            return EMPTY_RATE_STRING;
          }
          try {
            let fwdTracking = new BigNumber("1").dividedBy(markedUpRequestRt);
            let invTrigger = new BigNumber("1").dividedBy(tsd.initialTrailingRt);
            pips = fwdTracking.minus(invTrigger).absoluteValue().times(multiplier);
          } catch (error) {
            logger.out.warn("Error calculating trailingPips", error);
          }
        }
      }
    }
    if (pips) {
      return pips.toFixed(pipPrecision);
    }
    return EMPTY_RATE_STRING;
  }

  /**
   * Get the trailing pips value for Blotter in current blotter mode
   * @param order
   * @returns
   */
  getBlotterTrailingTrigger(order: CubiqTS.OrderDataImpl): string {
    const isBase = !order.isInvertedOrderFg || !this.modeIsDealt;
    return isBase ? this.getBaseTrailingTrigger(order) : this.getInvertTrailingTrigger(order);
  }

  getTrailingTrigger(order: CubiqTS.OrderDataImpl): BigNumber | null {
    let tsd = order != null ? (order.trailingStopDetails as CubiqTS.TrailingStopDetailsImpl) : null;

    if (tsd != null && tsd.initialTrailingRt instanceof CubiqTS.BigNumber) {
      return tsd.initialTrailingRt;
    }
    return null;
  }

  /**
   * Get base trailing trigger of order independent of display mode
   * @param order
   * @returns
   */
  public getBaseTrailingTrigger(order: CubiqTS.OrderDataImpl): string {
    return this.getBaseInvertTrailingTrigger(order, order.isInvertedOrderFg, false);
  }

  public getInvertTrailingTrigger(order: CubiqTS.OrderDataImpl): string {
    return this.getBaseInvertTrailingTrigger(order, !order.isInvertedOrderFg, true);
  }

  /**
   * common code for both base and invert
   * @param order Main order with TrailingStopDetails
   * @param invertTsd Should the TSD rates be inverted? Selects trigger rate
   * @param invertRates Is the output pair an inverted rate? Selects precision
   * @returns
   */
  private getBaseInvertTrailingTrigger(
    order: CubiqTS.OrderDataImpl,
    invertTsd: boolean,
    invertRates: boolean,
  ): string {
    let tsd = order != null ? (order.trailingStopDetails as CubiqTS.TrailingStopDetailsImpl) : null;

    if (
      tsd &&
      tsd.initialTrailingRt instanceof CubiqTS.BigNumber &&
      order.orderState != CubiqTS.OrderState.TRIGGERED &&
      order.orderState != CubiqTS.OrderState.MANUAL_PRICING &&
      !tsd.isTrackingActive
    ) {
      let ccyPair: any = this.getCcyPair(order);
      const triggerRt = invertTsd
        ? new BigNumber(1).dividedBy(tsd.initialTrailingRt)
        : tsd.initialTrailingRt;
      if (ccyPair) {
        let minPrecision: number = invertRates ? ccyPair.spotInvertDecimalsQty : ccyPair.pipsQty;
        let maxPrecision: number = invertRates
          ? ccyPair.fwdInvertDecimalsQty
          : ccyPair.fwdRateDecimalsQty;
        return this.roundRateToPrecision(triggerRt, minPrecision, maxPrecision);
      }
      return triggerRt.toString();
    }
    return EMPTY_RATE_STRING;
  }

  private getTrackingSide(order: CubiqTS.OrderDataImpl): CubiqTS.RateSideType {
    if (order.trackingSide === undefined) {
      return null;
    }
    let trackingSide: CubiqTS.RateSideType = order.trackingSide;
    if (
      order.requestedOrderType == CubiqTS.RequestOrderType.SL_ASK ||
      order.requestedOrderType == CubiqTS.RequestOrderType.SL_BID ||
      order.requestedOrderType == CubiqTS.RequestOrderType.TSL_ASK ||
      order.requestedOrderType == CubiqTS.RequestOrderType.TSL_BID
    ) {
      if (this.modeIsDealt) {
        if (trackingSide != null && trackingSide != undefined && order.isInvertedOrderFg) {
          if (trackingSide == CubiqTS.RateSideType.BID) {
            return CubiqTS.RateSideType.OFFER;
          } else if (trackingSide == CubiqTS.RateSideType.OFFER) {
            return CubiqTS.RateSideType.BID;
          }
        }
      }
      return trackingSide;
    }
    let isAboveMarket: boolean = this.isAboveMarket(order);
    let tg: CubiqTS.TradingGroupImpl = this.myTraderGroupLookup.getTraderGroup(order.deskId);
    let isAutoFill: boolean = tg ? tg.isAutoFilledFg : false;
    let crossed: boolean = this.isSpreadCrossedForOrder(
      order.baseCode,
      order.nonBaseCode,
      order.orderType,
      isAutoFill,
    );
    if (crossed) {
      return isAboveMarket ? CubiqTS.RateSideType.BID : CubiqTS.RateSideType.OFFER;
    }

    return isAboveMarket ? CubiqTS.RateSideType.OFFER : CubiqTS.RateSideType.BID;
  }

  private isSpreadCrossedForOrder(
    base: string,
    nonBase: string,
    orderType: CubiqTS.OrderType,
    isAuto: boolean,
  ): boolean {
    let pair: CubiqTS.CurrencyPairImpl = this.myCcyPairLookup.getCcyPair(base, nonBase);
    let isCrossed: boolean;
    if (pair) {
      if (isAuto) {
        if (orderType == CubiqTS.OrderType.LIMIT_SL) {
          isCrossed = pair.triggerAutoSlSpreadType == CubiqTS.TriggerLogicType.CROSSED;
        } else if (orderType == CubiqTS.OrderType.LIMIT_TP) {
          isCrossed = pair.triggerAutoTpSpreadType == CubiqTS.TriggerLogicType.CROSSED;
        }
        if (orderType == CubiqTS.OrderType.LIMIT_CALL) {
          isCrossed = pair.triggerAutoCallSpreadType == CubiqTS.TriggerLogicType.CROSSED;
        }
      } else {
        if (orderType == CubiqTS.OrderType.LIMIT_SL) {
          isCrossed = pair.triggerManSlSpreadType == CubiqTS.TriggerLogicType.CROSSED;
        } else if (orderType == CubiqTS.OrderType.LIMIT_TP) {
          isCrossed = pair.triggerManTpSpreadType == CubiqTS.TriggerLogicType.CROSSED;
        }
        if (orderType == CubiqTS.OrderType.LIMIT_CALL) {
          isCrossed = pair.triggerManCallSpreadType == CubiqTS.TriggerLogicType.CROSSED;
        }
      }
    } else {
      logger.out.error("No pair found to check tracking side [" + base + "/" + nonBase + "]");
    }
    return isCrossed;
  }

  /**
   * secondary sorting on order id will be preformed elsewhere
   */
  public calculatePinAdjustment(
    order: any,
    isAboveMarket: boolean,
    isAlgoNoTrackingRate: boolean,
    isPinTriggered: boolean,
  ): number {
    let sortAdj: number;

    if (isAboveMarket) {
      // Orders with lower orderID should be closer to market
      if (!this.isTriggered(order) || !isPinTriggered) {
        if (this.isMarketOrder(order)) {
          sortAdj = ABOVE_MKT_PENDING_BIN;
        } else if (isAlgoNoTrackingRate) {
          sortAdj = ABOVE_ALGO_ACTIVE_BIN;
        } else {
          sortAdj = ABOVE_ACTIVE_BIN;
        }
      } else {
        if (this.isMarketOrder(order)) {
          sortAdj = ABOVE_TRIGGER_MKT_BIN;
        } else {
          sortAdj = ABOVE_TRIGGER_BIN;
        }
      }
    } else {
      if (!this.isTriggered(order) || !isPinTriggered) {
        if (this.isMarketOrder(order)) {
          sortAdj = BELOW_MKT_PENDING_BIN;
        } else if (isAlgoNoTrackingRate) {
          sortAdj = BELOW_ALGO_ACTIVE_BIN;
        } else {
          sortAdj = BELOW_ACTIVE_BIN;
        }
      } else {
        if (this.isMarketOrder(order)) {
          sortAdj = BELOW_TRIGGER_MKT_BIN;
        } else {
          sortAdj = BELOW_TRIGGER_BIN;
        }
      }
    }
    return sortAdj;
  }

  isMarketOrder(order: CubiqTS.OrderDataImpl) {
    let ret = false;

    if (null != order && null != order.orderType) {
      ret =
        CubiqTS.OrderType.MARKET == order.orderType ||
        CubiqTS.OrderType.MARKET_FOK == order.orderType ||
        CubiqTS.OrderType.MARKET_IOC == order.orderType;
    }

    return ret;
  }

  isSpotOrder(order: CubiqTS.OrderDataImpl): boolean {
    return (
      CubiqTS.Product.SPT === order.product || CubiqTS.Tenor.SPOT === order.adjustedSettleTenor
    );
  }

  public static createStrikethroughValue(
    value: string | number,
    strikethrough: boolean,
  ): string | number {
    if (value == null) {
      return null;
    }

    if (typeof value === "string" && strikethrough === true) {
      return value + (strikethrough ? STRIKETHROUGH_SUFFIX : "");
    }

    // Perc away should max out at 100001
    if (typeof value === "number" && strikethrough === true && !isNaN(value)) {
      return value + STRIKETHROUGH_INCREASE;
    }

    return value;
  }

  public static isStrikeThroughValue(value: string | number): boolean {
    if (value == null) {
      return false;
    }

    if (typeof value === "string") {
      return value.endsWith(STRIKETHROUGH_SUFFIX);
    }

    // Perc away should max out at 100001
    if (typeof value === "number" && !isNaN(value)) {
      return value > STRIKETHROUGH_INCREASE / 2;
    }

    return false;
  }

  public static clearStrikeThroughString(value: string): string {
    if (value == null) {
      return null;
    }

    if (typeof value === "string") {
      return value.replace(STRIKETHROUGH_SUFFIX, "");
    }

    return value;
  }

  public static clearStrikeThroughValue(value: string | number): number {
    if (value == null) {
      return null;
    }

    if (typeof value === "string") {
      return Number.parseFloat(value.replace(STRIKETHROUGH_SUFFIX, ""));
    }

    // Perc away should max out at 100001
    if (typeof value === "number" && !isNaN(value)) {
      return value > STRIKETHROUGH_INCREASE / 2 ? value - STRIKETHROUGH_INCREASE : value;
    }

    return value;
  }
}
